import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap,
  BookOpen,
  Users,
  Award,
  ArrowRight,
  CheckCircle,
  Clock,
  Target,
  Microscope,
  Calculator,
  Book,
  Globe,
  Languages,
  ChevronRight,
  ChevronLeft,
  Sparkles,
  Trophy,
  Atom,
  FlaskConical,
  Brain,
  Rocket,
  Cpu,
  Dna,
  Router,
  FileText,
  Download,
  Eye
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Jauge circulaire animée façon instrument de laboratoire, avec compteur synchronisé
const ResultDial: React.FC<{ label: string; value: number; accent: string; delay?: number }> = ({ label, value, accent, delay = 0 }) => {
  const [count, setCount] = useState(0);
  const radius = 52;
  const circumference = 2 * Math.PI * radius;

  const startCount = () => {
    const duration = 1200;
    const start = performance.now();
    const tick = (now: number) => {
      const progress = Math.min((now - start) / duration, 1);
      setCount(Math.round(progress * value));
      if (progress < 1) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  };

  return (
    <motion.div
      className="flex flex-col items-center gap-3"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-40px' }}
      onViewportEnter={startCount}
      transition={{ duration: 0.6, delay }}
    >
      <div className="relative w-28 h-28 sm:w-36 sm:h-36">
        <svg viewBox="0 0 120 120" className="w-full h-full -rotate-90">
          <circle cx="60" cy="60" r={radius} fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="6" />
          <motion.circle
            cx="60" cy="60" r={radius} fill="none"
            stroke={accent}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            whileInView={{ strokeDashoffset: circumference - (value / 100) * circumference }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 1.2, delay, ease: 'easeOut' }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="font-mono font-bold text-white text-xl sm:text-3xl tabular-nums">{count}%</span>
        </div>
      </div>
      <span className="font-mono font-bold text-sm sm:text-base uppercase tracking-[0.1em] text-white text-center leading-tight">{label}</span>
    </motion.div>
  );
};

const Home: React.FC = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  const navigate = useNavigate();

  const handleNavigateToDocuments = () => {
    navigate('/documents');
  };

  // Niveaux d'études 
  const levels = [
    { 
      name: 'Sixième', 
      status: 'active', 
      year: '2025/2025', 
      color: 'from-blue-600 via-blue-700 to-indigo-800', 
      icon: Book,
      description: 'Fondations scientifiques solides',
      students: '25 élèves'
    },
    { 
      name: 'Cinquième', 
      status: 'active', 
      year: '2025/2025', 
      color: 'from-indigo-600 via-indigo-700 to-purple-800', 
      icon: Calculator,
      description: 'Approfondissement des mathématiques',
      students: '25 élèves'
    },
    { 
      name: 'Quatrième', 
      status: 'active', 
      year: '2025/2026', 
      color: 'from-emerald-600 via-emerald-700 to-teal-800', 
      icon: Microscope,
      description: 'Découverte des sciences expérimentales',
      students: '25 élèves'
    },
    { 
      name: 'Troisième', 
      status: 'active', 
      year: '2025/2026', 
      color: 'from-orange-600 via-orange-700 to-red-800', 
      icon: FlaskConical,
      description: 'Préparation aux séries scientifiques',
      students: '25 élèves'
    },
    { 
      name: 'Seconde', 
      status: 'active', 
      year: '2025/2026', 
      color: 'from-blue-600 via-blue-700 to-indigo-800', 
      icon: Atom,
      description: 'Spécialisation scientifique',
      students: '25 élèves'
    },
    { 
      name: 'Première S2 & S1', 
      status: 'active', 
      year: '2025/2026', 
      color: 'from-indigo-600 via-indigo-700 to-purple-800', 
      icon: Brain,
      description: 'Excellence académique',
      students: '25 élèves'
    },
    { 
      name: 'Terminale S2 & S1', 
      status: 'active', 
      year: '2025/2026', 
      color: 'from-emerald-600 via-emerald-700 to-teal-800', 
      icon: Rocket,
      description: 'Préparation aux grandes écoles',
      students: '25 élèves'
    }
  ];

  // Types de formations
  const formationTypes = [
    {
      title: 'Programme Scolaire Interne',
      description: 'Formation complète de la 6ème à la Terminale avec un programme scientifique renforcé',
      icon: GraduationCap,
      color: 'from-blue-600 to-indigo-700',
      features: ['Classes à effectif réduit', 'Suivi personnalisé', 'Préparation aux grandes écoles'],
      duration: 'Lun-Ven: 8h-16h',
      students: '50+ élèves'
    },
    {
      title: 'Programme de Renforcement Académique',
      description: 'Programmes intensifs pour approfondir les matières scientifiques et préparer les concours',
      icon: Clock,
      color: 'from-emerald-600 to-teal-700',
      features: ['Mathématiques avancées', 'Physique Chimie & SVT ...','Préparation concours'],
      duration: 'Week-ends',
      students: '200+ élèves'
    },
    {
      title: 'Accompagnement pédagogique(primaire)',
      description: 'Séances programmées les mercredis soirs et samedis matins',
      icon: BookOpen,
      color: 'from-green-500 to-emerald-600',
      features: [
        'Méthodes adaptées aux besoins spécifiques des enfants',
        'Un encadrement stimulant favorisant curiosité intellectuelle et confiance en soi'
      ],
      duration: 'Mercredi & Samedi',
      students: '30+ élèves'
    },
    {
      title: 'Formations sur Mesure',
      description: 'Programmes personnalisés pour les élèves en difficulté ou souhaitant se perfectionner',
      icon: Target,
      color: 'from-purple-600 to-violet-700',
      features: ['Évaluation personnalisée', 'Méthodes adaptées', 'Suivi régulier'],
      duration: 'Sur mesure',
      students: '50+ élèves'
    }
  ];

  const featured = formationTypes[0];
  const nonFeaturedFormations = formationTypes.filter(f => f.title !== featured.title);
  const [expandedFormationIndex, setExpandedFormationIndex] = useState<number | null>(null);

  // Statistiques 
  const stats = [
    { number: '100%', label: 'Taux de réussite au BFEM', icon: Trophy, color: 'from-yellow-500 to-orange-600' },
    { number: '200+', label: 'Élèves actifs', icon: Users, color: 'from-blue-500 to-indigo-600' },
    { number: '10', label: 'Années d\'excellence', icon: Award, color: 'from-purple-500 to-pink-600' }
  ];

  // Résultats des encadrements 2025-2026
  const examResults = [
    { label: 'CFEE', value: 100, accent: '#2DD4BF' },
    { label: 'BFEM', value: 100, accent: '#2DD4BF' },
    { label: 'BAC TS1', value: 100, accent: '#2DD4BF' },
    { label: 'BAC TS2', value: 86, accent: '#FBBF24' },
  ];

  // Matières scientifiques et littéraires
  const subjects = [
    { name: 'Mathématiques', icon: Calculator, color: 'from-blue-600 to-blue-700', description: 'Algèbre, géométrie, analyse' },
    { name: 'Physique', icon: Atom, color: 'from-green-600 to-green-700', description: 'Mécanique, électricité, optique' },
    { name: 'Chimie', icon: FlaskConical, color: 'from-purple-600 to-purple-700', description: 'Réactions, équilibres, synthèse' },
    { name: 'Sciences de la Vie', icon: Dna, color: 'from-red-600 to-red-700', description: 'Biologie, génétique, écologie' },
    { name: 'Informatique', icon: Cpu, color: 'from-indigo-600 to-indigo-700', description: 'Programmation, algorithmes' },
    { name: 'Français', icon: BookOpen, color: 'from-emerald-600 to-emerald-700', description: 'Littérature, grammaire, expression' },
    { name: 'Histoire-Géographie', icon: Globe, color: 'from-amber-600 to-amber-700', description: 'Histoire, géographie, géopolitique' },
    { name: 'Philosophie', icon: Brain, color: 'from-violet-600 to-violet-700', description: 'Réflexion, analyse, critique' },
    { name: 'Anglais', icon: Languages, color: 'from-teal-600 to-teal-700', description: 'Ecrire & Parler Anglais' },
    { name: 'Technologie', icon: Router, color: 'from-purple-600 to-purple-700', description: 'Discipline scientifique axée sur la technologie' },
    { name: 'Travaux Pratiques', icon: FlaskConical, color: 'from-emerald-600 to-emerald-700', description: 'Des travaux pratiques dès la 6e' },
    { name: 'Communication', icon: BookOpen, color: 'from-blue-600 to-blue-700', description: 'Des cours de communication en Français & Anglais' },
  ];

  // Images de la galerie des élèves pour la section découverte
  const studentImages = [
    { src: new URL('../assets/images/crac1.jpg', import.meta.url).href, alt: 'Élèves en classe', title: 'Classe de Sixième', subtitle: 'Environnement d\'apprentissage moderne' },
    { src: new URL('../assets/images/crac2.jpg', import.meta.url).href, alt: 'Laboratoire de sciences', title: 'Laboratoire de Physique', subtitle: 'Équipements de pointe' },
    { src: new URL('../assets/images/crac3.jpg', import.meta.url).href, alt: 'Élèves en cours', title: 'Cours de Mathématiques', subtitle: 'Excellence académique' },
    { src: new URL('../assets/images/crac4.jpg', import.meta.url).href, alt: 'Bibliothèque', title: 'Centre de Documentation', subtitle: 'Ressources pédagogiques' },
    { src: new URL('../assets/images/crac5.jpg', import.meta.url).href, alt: 'Élèves en groupe', title: 'Travaux Pratiques', subtitle: 'Apprentissage par l\'expérience' },
    { src: new URL('../assets/images/crac6.jpg', import.meta.url).href, alt: 'Cours en salle', title: 'Cours de Chimie', subtitle: 'Formation scientifique' }
  ];

  // Images pour le carrousel du hero 
  const heroImages = studentImages;

  // Auto-play carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, heroImages.length]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + heroImages.length) % heroImages.length);
  };

  const handleDocumentsClick = () => {
    navigate('/documents');
    setTimeout(() => {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }, 100);
  };

  const handleFormationsClick = () => {
    const element = document.getElementById('formations');
    if (element) {
      element.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  return (
    <div className="bg-white">
      {/* Hero Section  */}
      <section className="relative min-h-screen sm:h-screen -mt-[calc(theme(spacing.12))] lg:-mt-[calc(theme(spacing.16))] pt-16 sm:pt-12 lg:pt-16 pb-8 sm:pb-0 flex items-start sm:items-center justify-center overflow-hidden">
        {/* Background Image Carousel avec effet parallaxe */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentImageIndex}
            initial={{ opacity: 0, scale: 1.1 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 1.5, ease: "easeInOut" }}
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${heroImages[currentImageIndex].src})`,
              filter: 'brightness(0.6) contrast(1.1) saturate(1.1)'
            }}
          />
        </AnimatePresence>
        
        {/* Overlay gradient  */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-blue-900/30 pointer-events-none" />

        {/* Particules animées en arrière-plan */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {[...Array(12)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white/40 rounded-full"
              animate={{
                x: [0, 60, 0],
                y: [0, -60, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: 5 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 1.5,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>

        {/* Navigation Controls - Masqués sur mobile */}
        <div className="absolute inset-0 hidden md:flex items-center justify-between px-6 z-30 pointer-events-none">
          <motion.button
            onClick={prevImage}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
            className="bg-white/15 backdrop-blur-md rounded-full p-4 text-white hover:bg-white/25 transition-all duration-300 border border-white/30 cursor-pointer hover:cursor-pointer active:cursor-pointer pointer-events-auto"
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.25)' }}
            whileTap={{ scale: 0.9 }}
            style={{ cursor: 'pointer' }}
          >
            <ChevronLeft className="h-8 w-8" />
          </motion.button>
          
          <motion.button
            onClick={nextImage}
            onMouseEnter={() => setIsAutoPlaying(false)}
            onMouseLeave={() => setIsAutoPlaying(true)}
            className="bg-white/15 backdrop-blur-md rounded-full p-4 text-white hover:bg-white/25 transition-all duration-300 border border-white/30 cursor-pointer hover:cursor-pointer active:cursor-pointer pointer-events-auto"
            whileHover={{ scale: 1.1, backgroundColor: 'rgba(255,255,255,0.25)' }}
            whileTap={{ scale: 0.9 }}
            style={{ cursor: 'pointer' }}
          >
            <ChevronRight className="h-8 w-8" />
          </motion.button>
        </div>

        {/* Carousel Indicators */}
        <div className="absolute bottom-4 sm:bottom-8 left-1/2 transform -translate-x-1/2 flex space-x-2 sm:space-x-3 z-20">
          {heroImages.map((_, index) => (
            <motion.button
              key={index}
              onClick={() => setCurrentImageIndex(index)}
              className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${
                index === currentImageIndex 
                  ? 'bg-white shadow-lg scale-125' 
                  : 'bg-white/60 hover:bg-white/80'
              }`}
              whileHover={{ scale: 1.2 }}
              whileTap={{ scale: 0.9 }}
            />
          ))}
        </div>

        {/* Contenu principal du hero */}
        <div className="relative z-10 text-center text-white px-3 sm:px-6 max-w-6xl mx-auto w-full">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5 }}
            className="mb-4 sm:mb-8"
          >
            {/* Titre */}
            <motion.div
              className="mb-2 sm:mb-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.7 }}
            >
              <h1 className="text-3xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white tracking-wide sm:tracking-wider leading-tight drop-shadow-2xl">
                LES CRAC ACADEMY
              </h1>
              <div className="mt-2 sm:mt-3 mx-auto h-1 sm:h-1.5 w-48 sm:w-72 rounded-full bg-gradient-to-r from-blue-500 via-blue-300 to-transparent" />
            </motion.div>

            {/* Ligne ornementale + sous-titre */}
            <motion.div
              className="mb-3 sm:mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.9 }}
            >
              <div className="flex items-center justify-center gap-2 sm:gap-4 mb-1.5 sm:mb-2">
                <div className="h-px w-10 sm:w-20 bg-gradient-to-r from-transparent to-white/55" />
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-blue-300 flex-shrink-0" />
                <div className="h-px w-10 sm:w-20 bg-gradient-to-l from-transparent to-white/55" />
              </div>
              <p className="text-xs sm:text-lg md:text-xl lg:text-2xl text-blue-100 font-light tracking-widest uppercase">
                Lycée d'Excellence Scientifique
              </p>
              <div className="flex items-center justify-center gap-2 sm:gap-4 mt-1.5 sm:mt-2">
                <div className="h-px w-10 sm:w-20 bg-gradient-to-r from-transparent to-white/55" />
                <Sparkles className="h-3 w-3 sm:h-4 sm:w-4 text-blue-300 flex-shrink-0" />
                <div className="h-px w-10 sm:w-20 bg-gradient-to-l from-transparent to-white/55" />
              </div>
            </motion.div>

            {/* Bandeau inscriptions ouvertes */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 1.0 }}
              className="mx-auto mb-3 sm:mb-5 w-full max-w-xs sm:max-w-md"
            >
              <div className="relative overflow-hidden rounded-2xl bg-black/35 backdrop-blur-xl border border-amber-400/50 shadow-[0_0_50px_rgba(251,191,36,0.18)] px-3 sm:px-5 py-3 sm:py-4">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-400/10 via-transparent to-orange-500/5 pointer-events-none" />

                {/* En-tête */}
                <div className="relative flex items-center justify-center gap-2 mb-1.5">
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75" />
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />
                  </span>
                  <span className="text-amber-300 font-bold text-[10px] sm:text-xs uppercase tracking-widest">Inscriptions ouvertes</span>
                </div>

                <p className="relative text-white font-extrabold text-sm sm:text-base text-center leading-tight">
                  Tests d'entrée en cours
                </p>
              </div>
            </motion.div>

            {/* Palmarès */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1 }}
              className="mx-auto mb-6 sm:mb-8 flex flex-col sm:flex-row gap-3 items-stretch justify-center"
            >
              {/* 2024-2025 */}
              <div className="flex items-start gap-3 px-4 sm:px-5 py-4 rounded-2xl bg-white/15 backdrop-blur-md border border-white/25 shadow-xl text-left">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-white/20 border border-white/30">
                  <Trophy className="h-5 w-5 text-white" />
                </span>
                <div>
                  <p className="text-white/90 text-xs sm:text-sm leading-snug mb-2">
                    <span className="text-white font-extrabold">Seul Lauréat</span> aux Olympiades Nationales de Mathématiques 2024-2025, IEF de Guédiawaye
                  </p>
                  <div className="flex gap-2 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-950 shadow-md">
                      <GraduationCap className="h-3 w-3" />
                      Niveau 5ème
                    </span>
                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-950 shadow-md">
                      <GraduationCap className="h-3 w-3" />
                      Niveau 4ème
                    </span>
                  </div>
                </div>
              </div>

              {/* 2026 Collège */}
              <div className="flex items-start gap-3 px-4 sm:px-5 py-4 rounded-2xl bg-yellow-400/20 backdrop-blur-md border border-yellow-400/50 shadow-xl text-left">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-yellow-400/80 text-yellow-950">
                  <Trophy className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-white/90 text-xs sm:text-sm leading-snug mb-2">
                    <span className="text-yellow-300 font-extrabold">1ʳᵉ place</span> aux Olympiades Collégiennes de Mathématiques du Sénégal 2025-2026
                  </p>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-950 shadow-md">
                    <GraduationCap className="h-3 w-3" />
                    Niveau Collège
                  </span>
                </div>
              </div>

              {/* 2026 Lycée */}
              <div className="flex items-start gap-3 px-4 sm:px-5 py-4 rounded-2xl bg-yellow-400/20 backdrop-blur-md border border-yellow-400/50 shadow-xl text-left">
                <span className="flex-shrink-0 inline-flex items-center justify-center w-10 h-10 rounded-xl bg-yellow-400/80 text-yellow-950">
                  <Trophy className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-white/90 text-xs sm:text-sm leading-snug mb-2">
                    <span className="text-yellow-300 font-extrabold">1ʳᵉ place</span> aux Olympiades Académiques de Mathématiques 2025-2026, IA de Pikine-Guédiawaye
                  </p>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-gradient-to-r from-yellow-400 to-amber-500 text-yellow-950 shadow-md">
                    <GraduationCap className="h-3 w-3" />
                    Niveau Lycée
                  </span>
                </div>
              </div>
            </motion.div>
            
            <motion.div 
              className="flex flex-col sm:flex-row gap-2 sm:gap-4 justify-center items-center px-2 sm:px-4"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 1.4 }}
            >
              <motion.button
                onClick={handleDocumentsClick}
                className="group bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white px-4 sm:px-8 py-2.5 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-lg transition-all duration-300 shadow-2xl hover:shadow-blue-500/25 flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-center cursor-pointer hover:cursor-pointer active:cursor-pointer"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                style={{ cursor: 'pointer' }}
              >
                <span>Découvrir nos documents</span>
                <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              
              <motion.button
                onClick={handleFormationsClick}
                className="group bg-white/20 backdrop-blur-md border border-white/40 text-white px-4 sm:px-8 py-2.5 sm:py-4 rounded-lg sm:rounded-xl font-semibold text-sm sm:text-lg transition-all duration-300 hover:bg-white/30 flex items-center space-x-2 sm:space-x-3 w-full sm:w-auto justify-center shadow-xl cursor-pointer hover:cursor-pointer active:cursor-pointer"
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                style={{ cursor: 'pointer' }}
              >
                <span>Nos formations</span>
                <GraduationCap className="h-4 w-4 sm:h-5 sm:w-5 group-hover:rotate-12 transition-transform" />
              </motion.button>
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-6 right-4 sm:bottom-8 sm:right-8 z-20"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/70 rounded-full mt-2 animate-bounce"></div>
          </div>
        </motion.div>
      </section>

      {/* Section Résultats des encadrements 2025-2026 */}
      <section className="relative overflow-hidden py-14 sm:py-24 bg-[#0A1628]">
        {/* Trame façon papier millimétré */}
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{
            backgroundImage:
              'linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)',
            backgroundSize: '40px 40px',
          }}
        />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(37,99,235,0.28),transparent_65%)] pointer-events-none" />

        <div className="relative z-10 max-w-5xl mx-auto px-6">
          <motion.div
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 font-mono text-[10px] sm:text-xs uppercase tracking-[0.25em] text-teal-300/90 mb-4">
              <span className="relative flex h-1.5 w-1.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-teal-300 opacity-75" />
                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-teal-300" />
              </span>
              Session 2025/2026
            </div>
            <h2 className="text-3xl sm:text-5xl font-black text-white tracking-tight mb-4">
              Résultats aux examens
            </h2>
            <p className="text-blue-200/70 text-sm sm:text-base max-w-2xl mx-auto">
              LES CRAC ACADEMY a le plaisir de présenter les résultats des Encadrements CRAC de l'année scolaire 2025-2026.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-10 gap-x-4 sm:gap-6 place-items-center mb-14 sm:mb-16">
            {examResults.map((result, index) => (
              <ResultDial key={result.label} label={result.label} value={result.value} accent={result.accent} delay={index * 0.12} />
            ))}
          </div>

          <motion.p
            className="text-blue-200/60 text-xs sm:text-sm text-center max-w-2xl mx-auto mb-10 sm:mb-14"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.4 }}
          >
            Ces performances sont le fruit d'un encadrement rigoureux et d'une préparation méthodique aux examens nationaux.
          </motion.p>

          <motion.div
            className="flex justify-center mb-8"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-3 px-6 py-3 rounded-full bg-amber-400/15 border border-amber-400/50 shadow-[0_0_40px_rgba(251,191,36,0.15)]">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-400" />
              </span>
              <span className="font-mono font-bold text-base sm:text-2xl uppercase tracking-[0.12em] text-amber-300 whitespace-nowrap">
                Rentrée 2026/2027
              </span>
            </div>
          </motion.div>

          <motion.div
            className="text-center max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
          >
            <p className="text-white/90 text-sm sm:text-base font-medium mb-3">
              Ouverture aux élèves de la 6ème à la Terminale, exclusivement dans les séries scientifiques.
            </p>
            <p className="text-blue-200/55 text-xs sm:text-sm leading-relaxed">
              Notre mission : former des élèves d'excellence grâce à un enseignement exigeant, avec une préparation rigoureuse aux examens et concours nationaux (BFEM, Baccalauréat, Olympiades de Mathématiques, Miss, Concours général et concours scientifiques).
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section Fiche d'informations */}
      <section className="py-12 sm:py-16 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-blue-700 via-blue-800 to-indigo-900 shadow-2xl p-8 sm:p-10"
          >
            {/* Cercles décoratifs */}
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/5" />
            <div className="absolute -bottom-20 -left-10 w-72 h-72 rounded-full bg-indigo-500/10" />

            <div className="relative z-10 flex flex-col sm:flex-row items-start sm:items-center gap-6 sm:gap-10">
              {/* Icône */}
              <div className="flex-shrink-0 flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/15 border border-white/25 backdrop-blur-sm">
                <FileText className="h-8 w-8 sm:h-10 sm:w-10 text-white" />
              </div>

              {/* Texte */}
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-white/15 text-white/90 border border-white/25 mb-3">
                  Document officiel
                </div>
                <h2 className="text-xl sm:text-2xl md:text-3xl font-bold text-white mb-2">
                  Fiche d'informations de l'école
                </h2>
                <p className="text-blue-100/90 text-sm sm:text-base max-w-xl">
                  Retrouvez toutes les informations essentielles sur LES CRAC ACADEMY : programmes, tarifs, contacts et modalités d'inscription.
                </p>
              </div>

              {/* Boutons */}
              <div className="flex flex-col sm:flex-row gap-3 flex-shrink-0 w-full sm:w-auto">
                <a
                  href="/fiche-informations-crac-academy.pdf"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white text-blue-800 font-semibold text-sm hover:bg-blue-50 transition-colors shadow-lg"
                >
                  <Eye className="h-4 w-4" />
                  Consulter
                </a>
                <a
                  href="/fiche-informations-crac-academy.pdf"
                  download="Fiche d'informations LES CRAC ACADEMY.pdf"
                  className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/15 border border-white/30 text-white font-semibold text-sm hover:bg-white/25 transition-colors"
                >
                  <Download className="h-4 w-4" />
                  Télécharger
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section Statistiques  */}
      <section className="py-12 sm:py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-6">
              Une Excellence <span className="text-blue-600">Reconnue</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto">
              Nos résultats parlent d'eux-mêmes. LES CRAC ACADEMY forme les leaders scientifiques de demain.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 justify-items-center mx-auto">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center group"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-r ${stat.color} mb-4 group-hover:scale-110 transition-transform duration-300`}>
                  <stat.icon className="h-8 w-8 text-white" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">{stat.number}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Niveaux d'Études */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-6">
              Parcours <span className="text-blue-600">Scientifique</span> Complet
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto">
              De la 6ème à la Terminale, un cursus progressif et cohérent pour former les scientifiques de demain.
            </p>
          </motion.div>

          {/* Mobile: accordéon par niveau */}
          <div className="space-y-4 sm:hidden">
            {levels.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden"
              >
                <div className={`relative w-full flex items-center justify-between px-4 py-4`}>
                  <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br ${level.color}`}>
                      <level.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="text-base font-bold text-gray-900">{level.name}</div>
                      <div className="text-xs text-gray-600">{level.year} • {level.students}</div>
                    </div>
                  </div>
                  <button
                    onClick={handleDocumentsClick}
                    className="inline-flex items-center text-xs font-semibold text-blue-700 hover:text-blue-800"
                  >
                    Voir documents
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </button>
                </div>
                <div className="px-4 pb-4">
                  <p className="text-sm text-gray-700">{level.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Desktop/tablette: cartes blanches premium */}
          <div className="hidden sm:grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {levels.map((level, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="h-full"
              >
                <div className={`relative overflow-hidden rounded-2xl bg-white p-5 text-gray-900 shadow-xl border border-gray-200 transition-all duration-300 group h-full flex flex-col min-h-[220px]`}>
                  {/* Accent bar */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-600 to-indigo-700" />
                  {/* Step badge */}
                  <div className="absolute bottom-3 right-3 text-xs font-bold text-blue-700 bg-blue-50 border border-blue-200 rounded-full px-2 py-0.5">
                    Étape {index + 1}
                  </div>

                  <div className="relative z-10 flex flex-col h-full">
                    <div className="flex items-center justify-between mb-3">
                      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r ${level.color}`}>
                        <level.icon className="h-5 w-5 text-white" />
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-600">{level.year}</div>
                        <div className="text-[11px] text-gray-500">{level.students}</div>
                      </div>
                    </div>

                    <h3 className="text-lg font-bold mb-1">{level.name}</h3>
                    <p className="text-sm text-gray-700 mb-4 line-clamp-2">{level.description}</p>

                    <div className="mt-auto flex items-center justify-between pt-3 border-t border-gray-200">
                      <button
                        onClick={handleDocumentsClick}
                        className="inline-flex items-center gap-2 text-sm font-semibold text-blue-700 hover:text-blue-800"
                      >
                        Voir documents
                        <ArrowRight className="h-4 w-4" />
                      </button>
                      {level.status !== 'active' && (
                        <span className="text-[11px] bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                          Bientôt
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Types de Formations */}
      <section id="formations" className="py-12 sm:py-20 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-3 sm:mb-6">
              Formations <span className="text-blue-600">& Encadrement</span>
            </h2>
            <p className="text-base sm:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto">
              Des programmes adaptés à tous les besoins et tous les rythmes d'apprentissage.
            </p>
          </motion.div>

          {/* Carte mise en avant (Spotlight) */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative overflow-hidden rounded-3xl bg-white border border-blue-100 shadow-2xl p-6 sm:p-8 mb-8"
          >
            <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-blue-200/30 blur-3xl" />
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-indigo-200/30 blur-3xl" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-10 items-center">
              <div className="lg:col-span-2">
                <div className={`inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 rounded-2xl bg-gradient-to-r ${featured.color} mb-4`}>
                  <featured.icon className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
                </div>
                <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-900 mb-2">{featured.title}</h3>
                <p className="text-gray-600 text-sm sm:text-base mb-4 sm:mb-6 max-w-2xl">
                  {featured.description}
                </p>
                <div className="flex flex-wrap gap-2 sm:gap-3 mb-5 sm:mb-6">
                  {featured.features.map((f, i) => (
                    <span key={i} className="px-3 py-1.5 rounded-full text-xs sm:text-sm bg-blue-50 text-blue-700 border border-blue-200">
                      {f}
                    </span>
                  ))}
                </div>
                <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                  <button
                    onClick={handleDocumentsClick}
                    className="inline-flex items-center justify-center px-5 py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-sm sm:text-base font-semibold shadow-lg hover:shadow-xl transition-all"
                  >
                    Explorer les documents
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </button>
                  <div className="flex items-center text-xs sm:text-sm text-gray-600 px-3 py-2 bg-gray-50 rounded-xl border border-gray-200">
                    Planning: {featured.duration} • {featured.students}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 p-5">
                  <div className="text-sm font-semibold text-blue-800 mb-3">Pourquoi choisir ce programme ?</div>
                  <ul className="space-y-2 text-sm text-gray-700">
                    {featured.features.slice(0, 3).map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Mobile: cartes empilées (accordéon) */}
          <div className="space-y-4 sm:space-y-0 sm:hidden">
            {nonFeaturedFormations.map((formation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-2xl border border-gray-200 shadow-lg overflow-hidden"
              >
                <button
                  onClick={() => setExpandedFormationIndex(expandedFormationIndex === index ? null : index)}
                  className="w-full flex items-center justify-between px-4 py-4"
                >
                  <div className="flex items-center gap-3">
                    <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-r ${formation.color}`}>
                      <formation.icon className="h-5 w-5 text-white" />
                    </div>
                    <div className="text-left">
                      <div className="text-base font-bold text-gray-900">{formation.title}</div>
                      <div className="text-xs text-gray-600">{formation.duration} • {formation.students}</div>
                    </div>
                  </div>
                  <ChevronRight className={`h-5 w-5 text-gray-500 transition-transform ${expandedFormationIndex === index ? 'rotate-90' : ''}`} />
                </button>
                {expandedFormationIndex === index && (
                  <div className="px-4 pb-4">
                    <p className="text-sm text-gray-700 mb-3">{formation.description}</p>
                    <div className="flex flex-wrap gap-2 mb-3">
                      {formation.features.map((f, i) => (
                        <span key={i} className="px-3 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-200">{f}</span>
                      ))}
                    </div>
                    <button
                      onClick={handleDocumentsClick}
                      className="w-full inline-flex items-center justify-center px-4 py-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 text-white text-sm font-semibold shadow-md"
                    >
                      Explorer les documents
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </button>
                  </div>
                )}
              </motion.div>
            ))}
                  </div>
                  
          {/* Desktop/Tablet: liste défilante avec détails complets */}
          <div className="hidden sm:flex gap-4 sm:gap-5 overflow-x-auto pb-2 snap-x snap-mandatory">
            {nonFeaturedFormations.map((formation, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: index * 0.05 }}
                className="snap-start min-w-[260px] sm:min-w-[300px] lg:min-w-[0] lg:flex-1"
              >
                <div
                  className="bg-white rounded-2xl p-5 shadow-xl hover:shadow-2xl transition-all duration-300 border border-gray-100 h-full flex flex-col"
                  style={{ minHeight: '300px' }}
                >
                  <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-r ${formation.color} mb-4`}>
                    <formation.icon className="h-6 w-6 text-white" />
                  </div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">{formation.title}</h3>
                  <p className="text-gray-600 text-sm mb-3">{formation.description}</p>
                  <div className="space-y-2 mb-4">
                    {formation.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-2">
                        <CheckCircle className="h-4 w-4 text-green-600 mt-0.5" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-auto pt-3 border-t border-gray-100 flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {formation.duration} • {formation.students}
                    </div>
                    <button
                      onClick={handleDocumentsClick}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 hover:text-blue-800"
                    >
                      Découvrir
                      <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Section Matières Scientifiques et Littéraires */}
      <section className="py-12 sm:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold tracking-wide bg-blue-50 text-blue-700 border border-blue-200 mb-3">
              Pôle disciplinaire
            </div>
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-extrabold tracking-tight mb-2 sm:mb-3">
              <span className="bg-gradient-to-r from-blue-700 to-indigo-800 bg-clip-text text-transparent">Science</span>
              <span className="text-gray-900">, Technologie</span>
              <span className="bg-gradient-to-r from-indigo-700 to-blue-700 bg-clip-text text-transparent"> & Communication</span>
            </h2>
            <div className="w-16 sm:w-24 h-1.5 bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-600 rounded-full mx-auto mb-4 sm:mb-5" />
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-2xl sm:max-w-3xl mx-auto">
              Un programme complet et équilibré alliant excellence scientifique et culture générale.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {subjects.map((subject, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="group cursor-pointer"
                onClick={handleDocumentsClick}
              >
                <div className="relative bg-white rounded-2xl p-5 border border-gray-200 shadow-lg transition-all duration-300 overflow-hidden">
                  {/* Accent bar */}
                  <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-blue-600 to-indigo-700" />
                  <div className="relative">
                    <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-r ${subject.color} mb-3 group-hover:scale-105 transition-transform duration-300`}>
                      <subject.icon className="h-5 w-5 text-white" />
                  </div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">{subject.name}</h3>
                  <p className="text-gray-600 text-sm mb-4">{subject.description}</p>
                  <div className="flex items-center justify-between">
                      <button className="inline-flex items-center gap-1 text-sm font-semibold text-blue-700 group-hover:text-blue-800">
                        Voir documents
                        <ArrowRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
                      </button>
                    <span className="text-xs text-gray-500">Programme renforcé</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;